import { FC, useEffect, useLayoutEffect, useRef, useState } from "react";

import { Input } from "@/components/_atoms/input";
import { CUSTOM_DATA_SOURCE_PREFIX } from "@/domain/energy-source/lib/data-source-quality-mapping";
import { useGetInventoryValue } from "@/domain/inventory/hooks/api/use-get-inventory-value";
import { useDebounce } from "@/hooks/use-debounce";

interface RightAsideRowInputProps {
  inventoryValueId: string;
  placeholder: string;
  onValueChange?: (value: string) => void;
  dataSource?: string | null;
}

export const RightAsideRowInput: FC<RightAsideRowInputProps> = ({ inventoryValueId, placeholder, onValueChange, dataSource }) => {
  const { inventoryValue } = useGetInventoryValue({ id: dataSource === undefined ? inventoryValueId : "" });
  const currentSource = dataSource === undefined ? inventoryValue?.dataSource : dataSource;

  const [value, setValue] = useState<string>("");
  const isFocused = useRef(false);

  const onValueChangeRef = useRef(onValueChange);
  useLayoutEffect(() => {
    onValueChangeRef.current = onValueChange;
  }, [onValueChange]);
  const pendingChange = useRef<(() => void) | null>(null);
  const flushPendingChange = () => {
    const save = pendingChange.current;
    pendingChange.current = null;
    save?.();
  };
  const debouncedOnValueChange = useDebounce(flushPendingChange, 500);
  useLayoutEffect(() => () => {
    flushPendingChange();
    isFocused.current = false;
  }, [inventoryValueId]);

  useEffect(() => {
    if (isFocused.current) {
      return;
    }
    if (currentSource?.startsWith(`${CUSTOM_DATA_SOURCE_PREFIX}: `)) {
      setValue(currentSource.substring(`${CUSTOM_DATA_SOURCE_PREFIX}: `.length));
    } else {
      setValue("");
    }
  }, [currentSource, inventoryValueId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    pendingChange.current = () => onValueChangeRef.current?.(newValue);
    debouncedOnValueChange(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      flushPendingChange();
    }
  };

  const handleFocus = () => {
    isFocused.current = true;
  };

  const handleBlur = () => {
    isFocused.current = false;
    flushPendingChange();
  };

  return (
    <div className="flex w-full flex-row items-start gap-3 overflow-x-hidden">
      <Input
        type="text"
        placeholder={placeholder}
        aria-label={placeholder}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className="w-full text-xs"
      />
    </div>
  );
};
